import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Tables.css";
import { TableService } from "../../services/TableService";
import { TableTopicService } from "../../services/TableTopicService";
import LoadingAnimation from "../Loading/LoadingAnimation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTimes } from "@fortawesome/free-solid-svg-icons";
import Modal from "react-modal";
import ConfirmationModal from "../ConfirmationModal/ConfiramtionModal";
import TopicModal from "./TopicModal";
import ViewTopics from "./ViewTopics";
import Input from "../ui/input";

const Tables = () => {
  const navigate = useNavigate();

  interface Table {
    IdEventTable: number;
    TableName: string;
    Capacity: number;
    CostPerChair: number;
    Availability: number;
    // StartTime: string;
    // EndTime: string;
    Attachment: any;
    SaveName: string;
  }

  interface Topic {
    IdTableTopic: number;
    Topic: string;
    Description: string;
    StartTime: string;
    EndTime: string;
    Availabilities: { StartTime: string; EndTime: string }[];
  }

  const [tables, setTables] = useState<Table[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState<Table | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isConfirmDelete, setIsConfirmDelete] = useState(false);
  const [isTopicModalOpen, setIsTopicModalOpen] = useState(false);
  const [isTopicsModalOpen, setIsTopicsModalOpen] = useState(false);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<"success" | "error" | null>(
    null
  );
  const [selectedTableId, setSelectedTableId] = useState<number | null>(null);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleCardClick = (table: any) => {
    // Handle card click
  };

  const handleEditClick = (table: Table) => {
    setSelectedTable(table);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedTable) {
      const { name, value, type, checked } = e.target;

      if (name === "StartTime" || name === "EndTime") {
        const timePattern = /^([01]\d|2[0-3]):([0-5]\d)$/;
        if (timePattern.test(value)) {
          setSelectedTable({
            ...selectedTable,
            [name]: value,
          });
        } else {
          console.error("Invalid time format. Please use HH:mm format.");
        }
      } else if (name === "Availability") {
        setSelectedTable({
          ...selectedTable,
          [name]: checked ? 1 : 0,
        });
      } else {
        setSelectedTable({
          ...selectedTable,
          [name]: value,
        });
      }
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true); // Start loading
    const table = {
      ...selectedTable,
      Availability: selectedTable?.Availability ? true : false,
    };
    console.log("Selected Table:", selectedTable);
    try {
      const response = await TableService.updateTable(table);
      if (response.status === 200) {
        if (selectedTable) {
          setTables((prevTables) =>
            prevTables.map((t) =>
              t.IdEventTable === selectedTable.IdEventTable ? selectedTable : t
            )
          );
        }
        setMessage("Table updated successfully!");
        setMessageType("success");
      } else {
        console.error("Failed to update table:", response);
        setMessage("Failed to update table.");
        setMessageType("error");
      }
    } catch (error) {
      console.error("Error updating table:", error);
      setMessage("Error updating table.");
      setMessageType("error");
    } finally {
      setIsLoading(false);
      setIsModalOpen(false);
      setTimeout(() => {
        setMessage(null);
        setMessageType(null);
      }, 3000); // Clear message after 3 seconds
    }
  };

  const handleDeleteClick = () => {
    setIsModalOpen(false);
    setIsConfirmDelete(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);

    if (selectedTable) {
      try {
        const response = await TableService.deleteTable(
          selectedTable.IdEventTable
        );

        if (response.status === 200) {
          setTables((prevTables) =>
            prevTables.filter(
              (t) => t.IdEventTable !== selectedTable.IdEventTable
            )
          );
          setMessage("Table deleted successfully!");
          setMessageType("success");
        } else {
          console.error("Failed to delete table:", response);
          setMessage("Failed to delete table.");
          setMessageType("error");
        }
      } catch (error) {
        console.error("Error deleting table:", error);
        setMessage("Error deleting table.");
        setMessageType("error");
      } finally {
        setIsDeleting(false);
        setIsConfirmDelete(false);
        setIsModalOpen(false);
        setTimeout(() => {
          setMessage(null);
          setMessageType(null);
        }, 3000); // Clear message after 3 seconds
      }
    }
  };

  const handleAddTopicClick = (tableId: number) => {
    setSelectedTableId(tableId);
    setIsTopicModalOpen(true);
  };

  const handleViewTopicsClick = async (IdEventTable: number) => {
    try {
      const response = await TableTopicService.getTableTopics(IdEventTable);
      const topicsData = response.data.My_Result.map((topic: any) => ({
        IdTableTopic: topic.IdTableTopic,
        Topic: topic.Topic,
        Description: topic.Description,
        StartTime: topic.StartTime,
        EndTime: topic.EndTime,
        Availabilities: topic.Availabilities,
      }));
      setTopics(topicsData);
      setIsTopicsModalOpen(true);
      setSelectedTableId(IdEventTable); // Set the selected table ID
    } catch (error) {
      console.error("Error fetching table topics:", error);
    }
  };

  const handleTopicConfirm = (
    title: string,
    description: string,
    availabilities: { StartTime: string; EndTime: string }[]
  ) => {
    // Handle the topic description and time here
    console.log("Topic Title:", title);
    console.log("Topic Description:", description);
    console.log("Availabilities:", availabilities);
    setIsTopicModalOpen(false);
  };

  const handleTopicCancel = () => {
    setIsTopicModalOpen(false);
  };

  useEffect(() => {
    document.title = "Tables";
    const fetchTables = async () => {
      try {
        const response = await TableService.getTables(0, 0);
        console.log("Tables response:", response.data.My_Result);
        setTables(response.data.My_Result);
      } catch (error) {
        console.error("Error fetching tables:", error);
      }
    };
    fetchTables();
  }, []);

  const filteredTables = tables.filter(
    (table) =>
      table.TableName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      table.Availability.toString()
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
  );

  return (
    <div className="table-management">
      <div className="tables-header">
        <h1>Tables</h1>
        <button onClick={() => navigate("/tables-create")}>Create Table</button>
      </div>

      <div className="search-bar">
        <Input
          type="text"
          placeholder="Search tables"
          value={searchTerm}
          onChange={handleSearchChange}
        />
      </div>

      {message && (
        <div className={`message-bar ${messageType}`}>
          {message}
          <button
            className="close-btn"
            onClick={() => {
              setMessage(null);
              setMessageType(null);
            }}>
            ×
          </button>
        </div>
      )}
      <div className="table-grid">
        {isLoading ? (
          <LoadingAnimation />
        ) : filteredTables.length > 0 ? (
          filteredTables.map((table) => (
            <div
              key={table.IdEventTable}
              className="table-card"
              onClick={() => handleCardClick(table)}>
              <a
                href="#"
                className="tables-modal-add-topic"
                onClick={(e) => {
                  e.preventDefault();
                  handleViewTopicsClick(table.IdEventTable);
                }}>
                View Topics
              </a>
              <FontAwesomeIcon
                icon={faEdit}
                className="edit-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditClick(table);
                }}
              />
              <div className="card-header">
                <h3>{table.TableName}</h3>
              </div>
              <p>Capacity: {table.Capacity}</p>
              <p>Cost per chair: ${table.CostPerChair}</p>
              <p> {table.Availability ? "Available" : "Unavailable"}</p>
            </div>
          ))
        ) : (
          <LoadingAnimation />
        )}
      </div>

      {isModalOpen && selectedTable && (
        <Modal
          isOpen={isModalOpen}
          onRequestClose={handleModalClose}
          contentLabel="Edit Table"
          className="modal"
          overlayClassName="modal-overlay">
          <div className="bg-white p-4 rounded-2xl w-96 max-w-full relative">
            <button className="tables-modal-close" onClick={handleModalClose}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <form
              onSubmit={handleFormSubmit}
              className="flex flex-col space-y-4">
              <div className="text-2xl">Edit Table</div>
              <div className="form-group">
                <label htmlFor="TableName">Table Name:</label>
                <Input
                  id="TableName"
                  type="text"
                  name="TableName"
                  value={selectedTable.TableName}
                  onChange={handleFormChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="Capacity">Capacity:</label>
                <Input
                  id="Capacity"
                  type="number"
                  name="Capacity"
                  value={selectedTable.Capacity}
                  onChange={handleFormChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="CostPerChair">Cost per chair:</label>
                <Input
                  id="CostPerChair"
                  type="number"
                  name="CostPerChair"
                  value={selectedTable.CostPerChair}
                  onChange={handleFormChange}
                />
              </div>
              <div className="flex items-center space-x-2">
                {/* <div className="chip-list">
                  <span
                    className={`chip ${
                      selectedTable.Availability === 1 ? "available" : ""
                    }`}
                    onClick={() =>
                      handleFormChange({
                        target: {
                          name: "Availability",
                          value: "1",
                          type: "checkbox",
                          checked: true,
                        },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                    style={{
                      cursor: "pointer",
                      marginRight: "0.5rem",
                      padding: "0.3rem",
                      borderRadius: "5px",
                      backgroundColor:
                        selectedTable.Availability === 1
                          ? "#d0f0c0"
                          : "#f0f0f0",
                    }}>
                    Available
                  </span>
                  <span
                    className={`chip ${
                      selectedTable.Availability === 0 ? "unavailable" : ""
                    }`}
                    onClick={() =>
                      handleFormChange({
                        target: {
                          name: "Availability",
                          value: "0",
                          type: "checkbox",
                          checked: false,
                        },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                    style={{
                      cursor: "pointer",
                      padding: "0.3rem",
                      borderRadius: "5px",
                      backgroundColor:
                        selectedTable.Availability === 0
                          ? "#f8d7da"
                          : "#f0f0f0",
                    }}>
                    Unavailable
                  </span>
                </div> */}
                <label htmlFor="Availability">Availability:</label>
                <Input
                  type="checkbox"
                  name="Availability"
                  checked={selectedTable.Availability === 1}
                  className="scale-150 h-auto"
                  onChange={handleFormChange}
                />
              </div>

              <div className="flex items-center space-x-2">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn w-full ">
                  {isLoading ? <LoadingAnimation /> : "Save Changes"}
                </button>
                <button
                  type="button"
                  className="tables-modal-delete btn w-full"
                  onClick={handleDeleteClick}
                  disabled={isDeleting}>
                  {isDeleting ? "Deleting..." : "Delete Table"}
                </button>
              </div>
            </form>
          </div>
        </Modal>
      )}

      {isTopicsModalOpen && (
        <ViewTopics
          topics={topics}
          onClose={() => setIsTopicsModalOpen(false)}
          IdEventTable={selectedTableId ?? 0}
        />
      )}

      {isConfirmDelete && (
        <ConfirmationModal
          message="Are you sure you want to delete this table?"
          onConfirm={confirmDelete}
          onCancel={() => setIsConfirmDelete(false)}
        />
      )}

      {isTopicModalOpen && selectedTableId && (
        <TopicModal
          IdEventTable={selectedTableId}
          onConfirm={handleTopicConfirm}
          onCancel={handleTopicCancel}
        />
      )}
    </div>
  );
};

export default Tables;
